import { useEffect, useState } from 'react';
import type { IAltoDermaOthProcedureCustomControlData } from '../../../../../../common/validation/customControls/dermatology';
import type { ICustomControlProcedureReadProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcedureStatus } from '../../../../../../common/enums/procedureStatus';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { ProcedureReadButtons } from '../../../../elements/carePathButtons/ProcedureReadButtons';
import { FormDivider, FormParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { DermaOthV1ProcedureSummary } from './DermaOthV1ProcedureSummary';

export function DermaOthV1ProcedureRead(props: ICustomControlProcedureReadProps) {
    const { patient } = useEpisodeContext();
    const [isFooterVisible, setIsFooterVisible] = useState(false);

    const customControlData: IAltoDermaOthProcedureCustomControlData | undefined | null =
        props.procedure.customControlData;

    useEffect(() => {
        if (props.procedure.customControlData) {
            checkIsFooterVisible(props.procedure.customControlData);
        }
    }, [props.procedure.customControlData]);

    if (props.procedure.status !== ProcedureStatus.completed) {
        return null;
    }

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.procedure && props.process.procedure.dateSent}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerOrganization?.name
                        ? props.process.procedure.performerOrganization.name
                        : undefined
                }
                practitionerName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerPractitioner
                        ? getDisplayName(props.process.procedure.performerPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                showProcessIcon
                title={getServiceLabel(props.process.code, 'procedure', props.process.name)}
            />
            {customControlData && (
                <>
                    <DermaOthV1ProcedureSummary {...props} />

                    <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                        <FormDivider />

                        {customControlData.patient?.isSomeoneElse && (
                            <>
                                <HorizontalFormGroupRead>
                                    <FormLabel>Wie ben je?</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>
                                            Ik ben iemand anders en vul het voor de patiënt in
                                        </FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>

                                <HorizontalFormGroupRead>
                                    <FormLabel>Wat is je naam?</FormLabel>
                                    <InputContainer>
                                        <FormParagraph bold>{customControlData.patient.someoneElseName}</FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            </>
                        )}

                        <HorizontalFormGroupRead>
                            <FormLabel>Hoe voelt de aandoening</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>
                                    {isConditionVisible(customControlData) ? showCondition(customControlData) : '-'}
                                </FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Wanneer is het begonnen</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.conditionStarted ?? '-'}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Is het ondertussen veranderd</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.conditionChanged ?? '-'}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Is de kleur veranderd</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.conditionColorChanged ?? '-'}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Is de grootte veranderd</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.conditionSizeChanged ?? '-'}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>
                                Heeft {patient ? getDisplayName(patient, false, 'nameGivenNameFamily') : ''} deze
                                aandoening eerder gehad?
                            </FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.conditionBefore ?? '-'}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Wanneer en wat is er toen gedaan</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.conditionBeforeRemark ?? '-'}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Is er (zelf) al een behandeling geprobeerd?</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.treatingCondition ?? '-'}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Hoe behandel je deze aandoening</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.treatingConditionRemark ?? '-'}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>

                        <HorizontalFormGroupRead>
                            <FormLabel>Heb je een vraag of opmerking?</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{customControlData.extraQuestion ?? '-'}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    </CustomControlCollapseBody>

                    {isFooterVisible && (
                        <ProcessItemFooter
                            isItemOpen={props.itemState.isOpen}
                            labelLess={translateKey('procedure.hideProcedure')}
                            labelMore={translateKey('procedure.showProcedure')}
                            onToggle={async (isOpened) =>
                                await props.updateItemState({ ...props.itemState, isOpen: isOpened })
                            }
                        />
                    )}

                    <ProcedureReadButtons {...props} />
                </>
            )}
        </ProcessContainer>
    );

    function isConditionVisible(procedureCustomControlData: IAltoDermaOthProcedureCustomControlData): boolean {
        return (
            procedureCustomControlData.condition.jeukt ||
            procedureCustomControlData.condition.pijn ||
            !!procedureCustomControlData.condition.anders
        );
    }

    function showCondition(procedureCustomControlData: IAltoDermaOthProcedureCustomControlData): string {
        return [
            procedureCustomControlData.condition.jeukt ? 'Het jeukt' : '',
            procedureCustomControlData.condition.pijn ? 'Het doet pijn' : '',
            procedureCustomControlData.condition.anders,
        ]
            .filter((item) => item)
            .join(', ');
    }

    function checkIsFooterVisible(procedureCustomControlData: IAltoDermaOthProcedureCustomControlData): void {
        if (
            isConditionVisible(procedureCustomControlData) ||
            procedureCustomControlData.conditionStarted ||
            procedureCustomControlData.conditionChanged ||
            procedureCustomControlData.conditionColorChanged ||
            procedureCustomControlData.conditionSizeChanged ||
            procedureCustomControlData.conditionBefore ||
            procedureCustomControlData.conditionBeforeRemark ||
            procedureCustomControlData.treatingCondition ||
            procedureCustomControlData.treatingConditionRemark ||
            procedureCustomControlData.extraQuestion
        ) {
            setIsFooterVisible(true);
        }
    }
}
